import '../settings/settings-leaf.css';
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { companyApi, scheduleApi } from '../../api/endpoints.ts';
import { ApiRequestError } from '../../api/client.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, Select, TextInput } from '../ui.tsx';
import { formatNumericInstant } from '../../i18n/datetime.ts';

/**
 * Calendar feed management for Settings.
 *
 * A feed URL is a credential: it is displayed exactly once, at creation,
 * because only its hash is stored (same rule as quote share links). The list
 * shows label + last poll time so a leaked or unused feed can be spotted and
 * revoked.
 *
 * A feed can be scoped to one team member's assigned visits — the link each
 * cleaner subscribes to on their own phone, while the office keeps the
 * company-wide feed.
 */
export default function CalendarFeedsSection() {
  const { t } = useTranslation();
  const { can, locations } = useSession();
  const locationNameById = new Map(locations.map((location) => [location.id, location.name]));
  const [label, setLabel] = useState('');
  const [feedUserId, setFeedUserId] = useState('');
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const feeds = useQuery({ queryKey: ['calendar-feeds'], queryFn: () => scheduleApi.feeds() });
  const team = useQuery({ queryKey: ['team'], queryFn: () => companyApi.users() });
  const teamById = new Map((team.data?.items ?? []).map((member) => [member.id, member.fullName]));

  const create = useMutation({
    mutationFn: () =>
      scheduleApi.createFeed({
        ...(label ? { label } : {}),
        ...(feedUserId ? { userId: feedUserId } : {}),
      }),
    onSuccess: (result) => {
      setNewUrl(result.url);
      setLabel('');
      setFeedUserId('');
      setError(null);
      void feeds.refetch();
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('feeds.createError')),
  });

  const revoke = useMutation({
    mutationFn: (feedId: string) => scheduleApi.revokeFeed(feedId),
    onSuccess: () => {
      setError(null);
      void feeds.refetch();
    },
    onError: (caught) => setError(caught instanceof ApiRequestError ? caught.message : t('feeds.revokeError')),
  });

  async function copyUrl() {
    if (!newUrl) return;
    try {
      await navigator.clipboard.writeText(newUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError(t('feeds.copyError'));
    }
  }

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="feeds-heading">
      <div className="work-section-heading">
        <h2 id="feeds-heading" className="mb-3 text-lg font-semibold">
          {t('feeds.heading')}
        </h2>
      </div>
      <p className="mb-4 text-sm text-[color:var(--color-muted)]">{t('feeds.description')}</p>

      {error ? (
        <div className="mb-4">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {newUrl ? (
        <div className="mb-4">
          <Alert tone="success" title={t('feeds.createLinkTitle')}>
            <p className="mb-2">{t('feeds.createLinkMessage')}</p>
            <p className="mb-2 break-all font-mono text-xs">{newUrl}</p>
            <div className="flex gap-2">
              <Button onClick={() => void copyUrl()}>{copied ? t('common.copied') : t('common.copyLink')}</Button>
              <Button variant="ghost" onClick={() => setNewUrl(null)}>
                {t('feeds.done')}
              </Button>
            </div>
          </Alert>
        </div>
      ) : null}

      {feeds.data && feeds.data.items.length > 0 ? (
        <ul className="mb-4 divide-y divide-[color:var(--color-line)]">
          {feeds.data.items.map((feed) => (
            <li key={feed.id} className="flex flex-wrap items-center justify-between gap-3 py-2">
              <div>
                <p className="font-medium">
                  {feed.label ?? t('feeds.defaultLabel')}
                  {feed.userId ? (
                    <span className="ml-2 text-sm font-normal text-[color:var(--color-muted)]">
                      {t('feeds.scopedTo', { name: teamById.get(feed.userId) ?? t('feeds.formerTeamMember') })}
                    </span>
                  ) : null}
                  {/* Which visits the link actually carries. Only worth saying
                      to someone who works in more than one location — for
                      everybody else every feed is the same feed. */}
                  {locations.length > 1 ? (
                    <span className="ml-2 text-sm font-normal text-[color:var(--color-muted)]">
                      {feed.locationId
                        ? t('feeds.scopedToLocation', {
                            name: locationNameById.get(feed.locationId) ?? t('feeds.otherLocation'),
                          })
                        : t('feeds.everyLocation')}
                    </span>
                  ) : null}
                </p>
                <p className="text-sm text-[color:var(--color-muted)]">
                  {t('feeds.created', { date: formatNumericInstant(feed.createdAt) })}
                  {feed.lastAccessedAt
                    ? t('feeds.lastFetched', { date: formatNumericInstant(feed.lastAccessedAt) })
                    : t('feeds.neverFetched')}
                </p>
              </div>
              {can('schedule:write') ? (
                <Button
                  variant="danger"
                  busy={revoke.isPending}
                  onClick={() => {
                    if (window.confirm(t('feeds.confirmRevoke'))) revoke.mutate(feed.id);
                  }}
                >
                  {t('feeds.revoke')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : feeds.data ? (
        <p className="mb-4 text-[color:var(--color-muted)]">{t('feeds.emptyMessage')}</p>
      ) : null}

      {can('schedule:write') ? (
        <div className="flex flex-wrap items-start gap-2">
          <div className="min-w-60 flex-1">
            <TextInput
              label={t('feeds.labelField')}
              hint={t('feeds.labelHint')}
              value={label}
              onChange={(event) => setLabel(event.target.value)}
            />
          </div>
          <div className="min-w-52">
            <Select
              label={t('feeds.scopeField')}
              hint={t('feeds.scopeHint')}
              value={feedUserId}
              options={[
                { value: '', label: t('feeds.scopeWholeCompany') },
                ...(team.data?.items ?? [])
                  .filter((member) => member.isActive)
                  .map((member) => ({ value: member.id, label: member.fullName })),
              ]}
              onChange={(event) => setFeedUserId(event.target.value)}
            />
          </div>
          <div className="mb-4">
            {/* Invisible label matches the fields' label row so the button lines up with the inputs, not their hint text. */}
            <span className="field-label invisible" aria-hidden="true">
              {t('feeds.createFeedLink')}
            </span>
            <Button variant="primary" busy={create.isPending} onClick={() => create.mutate()}>
              {t('feeds.createFeedLink')}
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
